/**
 * @file     FollowUpOptIn.tsx
 * @purpose  The separate, unticked follow-up consent on a viewing request:
 *           "Relta Real Estate can follow up later about renting or buying." Its version rides on the request (stored server side), so
 *           an audit reads the exact wording. Never pre-ticked.
 */
'use client';

import { useId } from 'react';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import Typography from '@mui/material/Typography';
import { gateColors } from '@/components/gate/GateSheet';
import { VIEWING_FOLLOWUP_CONSENT_VERSION } from '@/core/config/viewingRequest';
import { useRentalsText } from './useRentalsText';

interface Props {
  checked: boolean;
  onChange: (checked: boolean) => void;
  /** Owner listings word it as "can also follow up", since Ray already answers the request. */
  recipient: 'mls' | 'owner';
  disabled?: boolean;
}

export function FollowUpOptIn({ checked, onChange, recipient, disabled = false }: Props) {
  const tr = useRentalsText();
  const labelId = useId();
  return (
    <Box
      component="label"
      data-testid="followup-optin"
      data-consent-version={VIEWING_FOLLOWUP_CONSENT_VERSION}
      sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, cursor: disabled ? 'default' : 'pointer', color: gateColors.textSecondary }}
    >
      <Checkbox
        name="followupOptIn"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
        size="small"
        inputProps={{ 'aria-labelledby': labelId }}
        sx={{ p: 0.5, ml: -0.5, mt: -0.25, color: 'inherit' }}
      />
      <Typography component="span" id={labelId} sx={{ fontSize: 13.5, lineHeight: 1.5, color: 'inherit' }}>
        {tr(recipient === 'owner' ? 'viewing.optInOwner' : 'viewing.optIn')}
      </Typography>
    </Box>
  );
}
